import { motion } from "framer-motion";
import { Building2, Crown, Mic, Users, ArrowUpRight } from "lucide-react";
import { EASE, SectionTag, MaskedLine, MagneticButton } from "./Motion";

const nodes = [
  { icon: Building2, label: "Brand", sub: "Briefs & budgets", testid: "pipeline-node-brand" },
  { icon: Crown, label: "BEATKINGSMM", sub: "Matchmaking engine", testid: "pipeline-node-beatking" },
  { icon: Mic, label: "Creator", sub: "12,000+ voices", testid: "pipeline-node-creator" },
  { icon: Users, label: "Audience", sub: "800M+ combined reach", testid: "pipeline-node-audience" },
];

const points = [
  "Fixed, transparent rate cards — no hidden brokerage",
  "Contracts, disclosures and compliance handled for you",
  "Post-campaign reports with real reach, not vanity metrics",
];

export default function BrandDeals() {
  return (
    <section id="brand-deals" data-testid="brand-deals-section" className="relative py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-16">
        <SectionTag index="04" label="Brand Deals Ecosystem" testid="brand-deals-tag" />
        <h2 className="mt-6 max-w-3xl text-4xl font-bold tracking-tight text-neutral-50 sm:text-5xl">
          <MaskedLine>Where brands meet</MaskedLine>
          <MaskedLine delay={0.12} innerClassName="font-serif italic font-medium text-gold-grad">
            believers.
          </MaskedLine>
        </h2>

        <div className="relative mt-24 hidden md:block">
          <div className="absolute left-[12%] right-[12%] top-8 h-px bg-white/10" />
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 1.6, ease: EASE }}
            className="absolute left-[12%] right-[12%] top-8 h-px origin-left bg-gradient-to-r from-goldlight via-gold to-goldbright shadow-[0_0_12px_rgb(var(--gold)_/_0.6)]"
            data-testid="brand-deals-line"
          />
          <span className="animate-travel-x absolute top-[29px] hidden h-1.5 w-1.5 rounded-full bg-goldlight shadow-[0_0_10px_2px_rgb(var(--gold-light)_/_0.8)] md:block" />
          <div className="relative grid grid-cols-4">
            {nodes.map((n, i) => {
              const Icon = n.icon;
              return (
                <motion.div
                  key={n.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.6 }}
                  transition={{ duration: 0.8, ease: EASE, delay: 0.3 + i * 0.35 }}
                  className="flex flex-col items-center text-center"
                  data-testid={n.testid}
                >
                  <div className="glass flex h-16 w-16 items-center justify-center rounded-full text-gold">
                    <Icon size={22} strokeWidth={1.75} />
                  </div>
                  <div className="mt-5 text-sm font-bold tracking-[0.15em] text-neutral-100">{n.label}</div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.2em] text-neutral-500">
                    {n.sub.toUpperCase()}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 items-center gap-14 md:grid-cols-2 md:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1, ease: EASE }}
          >
            <div className="bg-gradient-to-br from-gold/60 via-white/5 to-gold/20 p-px [clip-path:polygon(0_0,calc(100%-28px)_0,100%_28px,100%_100%,28px_100%,0_calc(100%-28px))]">
              <div className="relative overflow-hidden [clip-path:polygon(0_0,calc(100%-28px)_0,100%_28px,100%_100%,28px_100%,0_calc(100%-28px))]">
                <img
                  src="https://images.unsplash.com/photo-1668605335655-2dc1f8bec86b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxjaW5lbWF0aWMlMjBkYXJrJTIwc3R1ZGlvJTIwcG9ydHJhaXQlMjBjcmVhdG9yJTIwcG9kY2FzdHxlbnwwfHx8fDE3ODkxMTkyNTd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Creator in a cinematic studio"
                  className="aspect-[4/3] w-full object-cover"
                  loading="lazy"
                  data-testid="brand-deals-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 font-mono text-[10px] tracking-[0.3em] text-goldlight/80">
                  CREATOR SPOTLIGHT — 2.1M REACH
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1, ease: EASE, delay: 0.15 }}
          >
            <p className="font-serif text-2xl italic leading-snug text-neutral-200 sm:text-3xl">
              "They didn't sell us impressions. They introduced us to people who actually cared."
            </p>
            <p className="mt-4 font-mono text-[11px] tracking-[0.25em] text-neutral-500">
              — D2C FOUNDER, 3 CAMPAIGNS RUN
            </p>
            <ul className="mt-10 space-y-4">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-4 text-sm leading-relaxed text-neutral-300 sm:text-base">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <MagneticButton href="#contact" variant="ghost" testid="brand-deals-cta">
                Become a Partner <ArrowUpRight size={16} />
              </MagneticButton>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
